import React from 'react';
import { useAnalysisBacktest, type BacktestRuleSet } from 'hooks/useBacktest';
import type { BacktestProposal } from 'types/history';
import { BacktestChart } from './BacktestChart';
import { fmtPrice, fmtR, gradeTone, normalizeGrade, verdictLabel, verdictTone } from './backtestFormat';

interface BacktestPanelProps {
  analysisId: string;
  pair: BacktestRuleSet;
  onPairChange: (p: BacktestRuleSet) => void;
}

/** Backtest tab body: per-proposal outcome cards, each with a candle chart. */
export function BacktestPanel({ analysisId, pair, onPairChange }: BacktestPanelProps) {
  const { rollup, status, error } = useAnalysisBacktest(analysisId, pair);

  return (
    <div className="bt-panel">
      <div className="bt-panel-head">
        <div className="bt-window">
          <span
            className="bt-window-label"
            title="How long the limit order stays valid. This never closes a position — only the close window below can."
          >
            Entry window
          </span>
          <input
            className="bt-window-num"
            type="number"
            min={1}
            max={200}
            value={pair.window}
            onChange={(e) => onPairChange({ ...pair, window: Number(e.target.value) || 1 })}
            aria-label="Entry window"
          />
          <span
            className="bt-window-label"
            title="Time stop: a filled trade still unresolved after this many candles is closed at that bar's close. 0 = off, the position runs to TP or SL."
          >
            Close
          </span>
          <input
            className="bt-window-num"
            type="number"
            min={0}
            max={2000}
            value={pair.closeWindow}
            onChange={(e) => onPairChange({ ...pair, closeWindow: Math.max(0, Number(e.target.value) || 0) })}
            aria-label="Close window"
          />
          <span
            className="bt-window-label"
            title="Widen this proposal's stop to a multiple of its distance from the worst-case fill, leaving the targets where they are. 1 = the stop the analysis actually proposed. Raising it lowers R on every winner, so read the R below, not just the verdict."
          >
            SL ×
          </span>
          <input
            className="bt-window-num"
            type="number"
            min={1}
            max={5}
            step={0.25}
            value={pair.stopMult}
            onChange={(e) => onPairChange({ ...pair, stopMult: Math.min(5, Math.max(1, Number(e.target.value) || 1)) })}
            aria-label="Stop-loss distance multiplier"
          />
          <span className="bt-window-hint">
            candles{pair.closeWindow === 0 && ' · no time stop'}
            {pair.stopMult !== 1 && ` · stop ${pair.stopMult}× wider`}
          </span>
        </div>
        {rollup && (
          <div className="bt-meta">
            <span className={`bt-status bt-status-${rollup.status}`}>{rollup.status}</span>
            <span className="bt-meta-sep">·</span>
            <span>
              {rollup.market ?? '—'}
              {rollup.market_source === 'assumed_futures' && (
                <span className="bt-assumed" title="No job row — spot/futures assumed futures">
                  {' '}
                  (assumed)
                </span>
              )}
            </span>
            <span className="bt-meta-sep">·</span>
            <span>{rollup.candles_available} candles</span>
          </div>
        )}
      </div>

      {rollup?.data_note === 'mexc_spot_candles_for_futures' && (
        <div className="bt-note">
          MEXC analysis backtested against MEXC <b>spot</b> candles (MEXC's kline feed is spot) —
          near-stop outcomes can differ slightly from the perp.
        </div>
      )}

      {error && <div className="bt-error">{error}</div>}
      {!error && status === 'loading' && <div className="bt-loading">Running backtest…</div>}

      {!error && rollup && rollup.status === 'SKIPPED_NO_TRADES' && (
        <div className="bt-loading">This analysis has no trade proposals to backtest.</div>
      )}

      {!error && rollup && rollup.status === 'SKIPPED_MOCK' && (
        <div className="bt-loading">
          Mock analysis — its proposals were generated from synthetic candles, so they can&apos;t be
          backtested against real market data.
        </div>
      )}

      {!error &&
        rollup &&
        rollup.proposals.map((p) => (
          <ProposalCard key={p.trade_index} p={p} candles={rollup.candles ?? []} windowN={rollup.window_N} />
        ))}
    </div>
  );
}

type CancelledBy = NonNullable<NonNullable<BacktestProposal['no_entry']>['cancelled_by']>;

/** One clause, no trailing period, for the "Order cancelled — …" sentence. */
function cancelledCopy(c: CancelledBy): string {
  const bar = `candle ${c.bar_offset}`;
  if (c.rule === 'invalidation_close') {
    return `${bar} closed beyond the invalidation level (${fmtPrice(c.level)}) before the entry printed`;
  }
  if (c.rule === 'stop_before_entry') {
    const how = c.same_bar_as_entry
      ? c.resolved_intrabar
        ? 'the stop printed before the zone inside the same candle (sub-candles checked)'
        : 'the same candle spanned both the stop and the zone, arriving from the stop side'
      : 'the stop level printed before the zone';
    return `${how}, at ${bar}`;
  }
  return `${c.rule} at ${bar}`;
}

function ProposalCard({
  p,
  candles,
  windowN,
}: {
  p: BacktestProposal;
  candles: BacktestPanelCandles;
  windowN: number;
}) {
  const tone = verdictTone(p.verdict);
  const showChart = candles.length > 0 && p.entry_zone != null;
  const ev = p.no_entry?.eventual ?? null;

  // Mirrors the marker resolution in BacktestChart — whether either marker lands
  // beyond the scored window, which is what the legend below explains.
  const hasEventualEntry = p.fill_bar_offset == null && ev != null;
  const hasEventualExit =
    (p.verdict === 'OPEN' &&
      p.eventual_exit != null &&
      p.fill_bar_offset != null &&
      p.eventual_exit.outcome_bar_offset != null) ||
    (ev != null && ev.outcome_bar_offset != null);

  return (
    <div className={`bt-card bt-card-${tone}`}>
      <div className="bt-card-head">
        <span className={`bt-badge bt-badge-${tone}`}>
          {verdictLabel(p.verdict)}
          {p.verdict === 'TP_HIT' && p.tp_label ? ` ${p.tp_label}` : ''}
          {p.ambiguous_bar ? ' *' : ''}
        </span>
        <span className="bt-card-title">
          #{p.rank ?? '—'} {p.direction}
          {/* Tinted with the same scale the table's grade uses, so a Best call
              and a High Risk one are told apart at a glance here too. The raw
              label is shown rather than the slug — this is the drill-down, and
              it should read as the engine wrote it. */}
          {p.risk_label ? <span className={`bt-grade bt-tone-${gradeTone(normalizeGrade(p.risk_label))}`}>· {p.risk_label}</span> : ''}
          {p.nearest_entry ? ' · nearest' : ''}
        </span>
        <span className="bt-card-r">
          {p.realized_R != null ? fmtR(p.realized_R) : p.unrealized_R != null ? `${fmtR(p.unrealized_R)} (unreal.)` : ''}
        </span>
      </div>

      <div className="bt-levels">
        <span>
          Entry <b>{p.entry_zone ? `${fmtPrice(p.entry_zone[0])}–${fmtPrice(p.entry_zone[1])}` : '—'}</b>
        </span>
        <span>
          Stop <b>{fmtPrice(p.stop_loss)}</b>
        </span>
        {p.targets.map((t, i) => (
          <span key={i}>
            {t.label ?? `TP${i + 1}`} <b>{fmtPrice(t.price)}</b>
            {t.rr != null ? <span className="bt-muted"> (1:{t.rr})</span> : null}
          </span>
        ))}
      </div>

      {p.invalid_reason && (
        <div className="bt-detail bt-muted">Not evaluated: {p.invalid_reason.replace(/_/g, ' ')}</div>
      )}

      {p.filled && (
        <div className="bt-detail">
          Filled at candle {p.fill_bar_offset} ({fmtPrice(p.fill_price)})
          {p.outcome_bar_offset != null && (
            <>
              {' → '}
              {p.verdict === 'TP_HIT' ? 'hit ' + (p.tp_label ?? 'TP') : p.verdict === 'SL_HIT' ? 'hit stop' : 'resolved'}
              {' '}
              {p.outcome_bar_offset} candle{p.outcome_bar_offset === 1 ? '' : 's'} later ({fmtPrice(p.exit_price)})
            </>
          )}
          {p.ambiguous_bar && <span className="bt-muted"> — one candle spanned both TP and SL; scored stop-first.</span>}
          {p.verdict === 'OPEN' && (
            <span className="bt-muted">
              {' '}
              — never hit TP or SL in {candles.length} candles. MFE {fmtR(p.mfe_R)} / MAE{' '}
              {fmtR(p.mae_R)}.
            </span>
          )}
          {p.verdict === 'PENDING_INSUFFICIENT_DATA' && (
            <span className="bt-muted"> — outcome not decided yet; too few candles so far.</span>
          )}
          {/* Legacy rows (scored before the outcome scan was unbounded) still carry
              a separate eventual_exit. New results fold it into the verdict. */}
          {p.verdict === 'OPEN' && p.eventual_exit && (
            <span className="bt-eventual">
              {' '}
              Beyond the window it <b>{p.eventual_exit.verdict === 'TP_HIT' ? `hit ${p.eventual_exit.tp_label ?? 'TP'}` : 'hit the stop'}</b>
              {p.eventual_exit.outcome_bar_offset != null
                ? ` ${p.eventual_exit.outcome_bar_offset} candle${p.eventual_exit.outcome_bar_offset === 1 ? '' : 's'} after entry`
                : ''}
              {p.eventual_exit.realized_R != null ? ` (${fmtR(p.eventual_exit.realized_R)})` : ''}.
            </span>
          )}
        </div>
      )}

      {p.verdict === 'NO_ENTRY' && p.no_entry && (
        <div className="bt-detail">
          {p.no_entry.cancelled_by ? (
            <span>
              <b>Order cancelled</b> — {cancelledCopy(p.no_entry.cancelled_by)}. Never a position, so 0R.{' '}
            </span>
          ) : (
            <span>No fill in the first {windowN} candles. </span>
          )}
          Price went {p.no_entry.final_close_vs_zone ?? '—'} the zone (MFE{' '}
          {fmtR(p.no_entry.mfe_R)} / MAE {fmtR(p.no_entry.mae_R)}).{' '}
          {p.no_entry.market_entry_outcome && (
            <span>
              A market entry at the open would have{' '}
              <b>
                {p.no_entry.market_entry_outcome === 'TP_HIT'
                  ? `hit ${p.no_entry.market_entry_tp_label ?? 'TP'}`
                  : p.no_entry.market_entry_outcome === 'SL_HIT'
                    ? 'hit the stop'
                    : 'stayed open'}
              </b>
              .{' '}
            </span>
          )}
          {ev ? (
            <span className="bt-eventual">
              The limit <b>did fill later</b> at candle {ev.fill_bar_offset} ({fmtPrice(ev.fill_price)}) →{' '}
              <b>
                {ev.verdict === 'TP_HIT'
                  ? `hit ${ev.tp_label ?? 'TP'}`
                  : ev.verdict === 'SL_HIT'
                    ? 'hit the stop'
                    : 'still open'}
              </b>
              {ev.outcome_bar_offset != null
                ? ` ${ev.outcome_bar_offset} candle${ev.outcome_bar_offset === 1 ? '' : 's'} later`
                : ''}
              {ev.realized_R != null ? ` (${fmtR(ev.realized_R)})` : ''}.
            </span>
          ) : (
            <span className="bt-muted">Never filled within {p.no_entry.entry_search_candles ?? '—'} candles.</span>
          )}
        </div>
      )}

      {showChart && <BacktestChart candles={candles} proposal={p} windowN={windowN} />}

      {/* The badge verdict is window-scoped, so a chart can legitimately show an SL
          marker under an "OPEN" badge. Say so wherever such a marker is drawn. */}
      {showChart && (hasEventualEntry || hasEventualExit) && (
        <div className="bt-chart-legend">
          <span className="bt-eventual">Amber marker</span> = outside the {windowN}-candle
          window the verdict is scored on.
        </div>
      )}
    </div>
  );
}

type BacktestPanelCandles = React.ComponentProps<typeof BacktestChart>['candles'];
